import {each, generate, last, reduce} from '../lang/array';
import {times} from '../lang/function';
import {ceil, floor, pow} from '../lang/number';

const
    rotate = (x, n) => (
        (x >>> n) | (x << (32 - n))),

    scramble1 = x => (
        rotate(x, 2) ^
        rotate(x, 13) ^
        rotate(x, 22)),

    scramble2 = x => (
        rotate(x, 6) ^
        rotate(x, 11) ^
        rotate(x, 25)),

    scramble3 = x => (
        rotate(x, 7) ^
        rotate(x, 18) ^
        (x >>> 3)),

    scramble4 = x => (
        rotate(x, 17) ^
        rotate(x, 19) ^
        (x >>> 10)),

    choice = (x, y, z) => (
        (x & y) ^
        (~x & z)),

    majority = (x, y, z) => (
        (x & y) ^
        (x & z) ^
        (y & z)),

    hex = n => (
        generate(
            8,
            i => (0xf & (n >>> ((7 - i) * 4))).toString(16))
        .join('')),

    blocks = string => {
        const
            encoded = (
                unescape(
                    encodeURIComponent(
                        string))),
            message = encoded + String.fromCharCode(0x80),
            blocks = (
                generate(
                    ceil(((message.length / 4) + 2) / 16),
                    i => (
                        generate(16, j => {
                            const offset = ((i * 64) + (j * 4));

                            return (
                                (message.charCodeAt(offset + 0) << 24) |
                                (message.charCodeAt(offset + 1) << 16) |
                                (message.charCodeAt(offset + 2) << 8) |
                                (message.charCodeAt(offset + 3) << 0));
                        }))));

        last(blocks)[14] = floor((encoded.length * 8) / pow(2, 32));
        last(blocks)[15] = 0xffffffff & (encoded.length * 8);
        return blocks;
    },

    waves = block => {
        const wave = new Array(64);

        times(16, function(i) {
            wave[i] = block[i];
        });
        times(64 - 16, function(i) {
            i += 16;
            wave[i] = (
                0xffffffff & (
                    scramble4(wave[i - 2]) +
                    wave[i - 7] +
                    scramble3(wave[i - 15]) +
                    wave[i - 16]));
        });
        return wave;
    },

    constants = [
        0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
        0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
        0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
        0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
        0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
        0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
        0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
        0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
        0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
        0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
        0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
        0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
        0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
        0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
        0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
        0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
    ],

    seeds = [
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
        0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19
    ],

    hash = string => (
        reduce(
            blocks(string),
            (hashes, block) => {
                const next = hashes.slice();

                each(waves(block), (wave, i) => {
                    const
                        x = (
                            0xffffffff & (
                                next[7] +
                                scramble2(next[4]) +
                                choice(next[4], next[5], next[6]) +
                                constants[i] +
                                wave)),
                        y = (
                            0xffffffff & (
                                scramble1(next[0]) +
                                majority(next[0], next[1], next[2])));

                    each(
                        [
                            0xffffffff & (x + y),
                            next[0],
                            next[1],
                            next[2],
                            0xffffffff & (next[3] + x),
                            next[4],
                            next[5],
                            next[6]
                        ],
                        (value, i) => {
                            next[i] = value;
                        });
                });
                each(next, (hash, i) => {
                    hashes[i] = 0xffffffff & (hashes[i] + hash);
                });
                return hashes;
            },
            seeds.slice())),

    digest = string => (
        reduce(
            hash(string),
            (string, hash) => `${string}${hex(hash)}`,
            '')),

    bytes = string => (
        reduce(
            hash(string),
            (bytes, hash) => {
                bytes.push(0xff & (hash >> 24));
                bytes.push(0xff & (hash >> 16));
                bytes.push(0xff & (hash >> 8));
                bytes.push(0xff & hash);
                return bytes;
            },
            []));

export {
    digest,
    bytes
};
